import type { SeoOrFaviconTag } from 'react-datocms'
import type { MetaDescriptor } from 'react-router'
import type { Language } from './languageService.js'
import { DEFAULT_LANGUAGE, languageService, SUPPORTED_LANGUAGES } from './languageService.js'

export const BASE_URL = 'https://mudstudios.com'

/**
 * DatoCMS `_seoMetaTags` -> React Router `meta` descriptors.
 * (Replaces react-datocms' `toNextMetadata`.)
 */
export function seoTagsToMeta(tags: SeoOrFaviconTag[] | undefined): MetaDescriptor[] {
  return (tags || []).map(t => {
    if (t.tag === 'title') return { title: t.content ?? '' }
    if (t.tag === 'meta') return { ...t.attributes }
    return { tagName: 'link', ...t.attributes }
  })
}

/**
 * Self-referential canonical + reciprocal hreflang links for a canonical path
 * viewed in `lang`. Only languages with a real translation are advertised -
 * never point hreflang at a noindex fallback duplicate.
 */
export function alternatesMeta(canonicalPath: string, lang: Language): MetaDescriptor[] {
  const link = (attrs: Record<string, string>): MetaDescriptor => ({ tagName: 'link', ...attrs })

  const meta = [
    link({ rel: 'canonical', href: BASE_URL + languageService.localizePath(canonicalPath, lang) }),
  ]
  for (const l of SUPPORTED_LANGUAGES) {
    if (languageService.isTranslated(canonicalPath, l)) {
      meta.push(
        link({
          rel: 'alternate',
          // React prop casing (rendered through <Meta/>); HTML attribute names
          // are case-insensitive, so crawlers read the emitted hrefLang fine.
          hrefLang: l,
          href: BASE_URL + languageService.localizePath(canonicalPath, l),
        }),
      )
    }
  }
  meta.push(
    link({
      rel: 'alternate',
      hrefLang: 'x-default',
      href: BASE_URL + languageService.localizePath(canonicalPath, DEFAULT_LANGUAGE),
    }),
  )
  return meta
}

export const noindexMeta: MetaDescriptor = { name: 'robots', content: 'noindex' }

/** A localized page with no real translation is a fallback duplicate - noindex it. */
export function noindexUnlessTranslated(canonicalPath: string, lang: Language): MetaDescriptor[] {
  return languageService.isTranslated(canonicalPath, lang) ? [] : [noindexMeta]
}

/**
 * Merge ancestor meta (usually the root route's site defaults) with a route's
 * own; on collision (same title/name/property/link identity) the route wins.
 * React Router replaces ancestor meta entirely, so routes that only *add* to
 * the defaults (e.g. noindex or hreflang) use this to keep them.
 */
export function mergeMeta(base: MetaDescriptor[], overrides: MetaDescriptor[]): MetaDescriptor[] {
  const overridden = new Set(overrides.map(metaKey))
  return [...base.filter(d => !overridden.has(metaKey(d))), ...overrides]
}

function metaKey(d: MetaDescriptor): string {
  const desc = d as Record<string, unknown>
  if ('title' in desc) return 'title'
  if (typeof desc['name'] === 'string') return `name:${desc['name']}`
  if (typeof desc['property'] === 'string') return `property:${desc['property']}`
  if (desc['tagName'] === 'link') {
    const rel = desc['rel'] as string | undefined
    const hrefLang = desc['hrefLang'] as string | undefined
    return `link:${rel}:${hrefLang ?? ''}`
  }
  return JSON.stringify(d)
}
